import { useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatUSD, formatDate } from "@/lib/api";
import { Pencil, Trash2, Receipt, User, Building2, Tags, Wallet, Calendar, ArrowDownCircle, MessageSquare, Hash } from "lucide-react";

/**
 * Slide-out sheet showing every piece of metadata attached to a transaction.
 * Powers the "click the description → drill in" pattern on the Ledger.
 */
export default function TxnQuickView({ txn, open, onOpenChange, onEdit, onDelete, onDonorClick }) {
  if (!txn) return null;
  const isIncome = txn.type === "income";
  const gross = Number(txn.amount || 0);
  const fee = Number(txn.fee || 0);
  const net = isIncome ? gross - fee : gross;

  const Row = ({ icon: Icon, label, value, testid, onClick }) => {
    if (value === null || value === undefined || value === "") return null;
    const inner = (
      <>
        <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="text-sm text-foreground break-words">{value}</div>
        </div>
      </>
    );
    return onClick ? (
      <button onClick={onClick} className="flex items-start gap-3 py-2 w-full text-left hover:bg-secondary/50 rounded-xl px-2 -mx-2 transition-colors" data-testid={testid}>
        {inner}
      </button>
    ) : (
      <div className="flex items-start gap-3 py-2" data-testid={testid}>{inner}</div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg w-full overflow-y-auto rounded-l-3xl" data-testid="txn-quick-view">
        <SheetHeader className="pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isIncome ? "bg-primary/10 text-primary" : "bg-expense/10 text-expense"}`}>
              {isIncome ? <ArrowDownCircle className="w-6 h-6" /> : <Receipt className="w-6 h-6" />}
            </div>
            <div className="flex-1 text-left">
              <SheetTitle className="font-serif text-2xl leading-tight">
                <span className={`tabular-nums font-mono ${isIncome ? "text-primary" : "text-expense"}`} data-testid="txn-qv-amount">
                  {isIncome ? "+" : "−"}{formatUSD(isIncome ? net : gross)}
                </span>
              </SheetTitle>
              <SheetDescription asChild>
                <div>
                  <Badge variant="outline" className={isIncome ? "rounded-full bg-primary/10 text-primary border-primary/20" : "rounded-full bg-expense/10 text-expense border-expense/20"}>
                    {txn.type}
                  </Badge>
                  {txn.auto_generated && <Badge variant="outline" className="rounded-full ml-2 text-[10px]">auto-generated</Badge>}
                </div>
              </SheetDescription>
            </div>
          </div>
          {isIncome && fee > 0 && (
            <div className="mt-3 rounded-xl bg-secondary/50 p-3 text-xs" data-testid="txn-qv-fee-breakdown">
              <div className="flex justify-between"><span className="text-muted-foreground">Gross</span><span className="font-mono tabular-nums">{formatUSD(gross)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Fees</span><span className="font-mono tabular-nums text-destructive">−{formatUSD(fee)}</span></div>
              <div className="flex justify-between mt-1 pt-1 border-t border-border"><span className="font-medium">Net deposit</span><span className="font-mono tabular-nums font-semibold text-primary">{formatUSD(net)}</span></div>
            </div>
          )}
        </SheetHeader>

        <div className="mt-4 divide-y divide-border">
          <Row icon={Calendar} label="Date" value={formatDate(txn.date)} testid="txn-qv-date" />
          {txn.date_deposited && (
            <Row icon={ArrowDownCircle} label="Date deposited" value={formatDate(txn.date_deposited)} testid="txn-qv-date-deposited" />
          )}
          <Row icon={Tags} label="Category" value={txn.category_name || "—"} testid="txn-qv-category" />
          <Row icon={Wallet} label="Fund" value={txn.fund_name || "—"} testid="txn-qv-fund" />
          {isIncome
            ? <Row icon={User} label="Donor" value={txn.donor_name || "Anonymous"} testid="txn-qv-donor"
                   onClick={txn.donor_id && onDonorClick ? () => onDonorClick(txn.donor_id) : null} />
            : <Row icon={Building2} label="Vendor" value={txn.vendor_name || "—"} testid="txn-qv-vendor" />}
          <Row icon={MessageSquare} label="Description" value={txn.description || "—"} testid="txn-qv-description" />
          {txn.payment_method && <Row icon={Wallet} label="Payment method" value={txn.payment_method} testid="txn-qv-method" />}
          {txn.reference && <Row icon={Hash} label="Reference" value={txn.reference} testid="txn-qv-reference" />}
          {txn.origin && <Row icon={Hash} label="Origin" value={txn.origin} testid="txn-qv-origin" />}
        </div>

        <div className="flex gap-2 mt-6 pt-4 border-t border-border">
          {onEdit && (
            <Button variant="outline" className="flex-1 rounded-2xl" onClick={() => { onEdit(txn); onOpenChange(false); }} data-testid="txn-qv-edit">
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
          )}
          {onDelete && (
            <Button variant="outline" className="flex-1 rounded-2xl text-destructive hover:text-destructive" onClick={() => { onDelete(txn); onOpenChange(false); }} data-testid="txn-qv-delete">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
